// Wire-format types for the Log Inquiry feature (FEATURE_050).
// Matches the REAL backend contract: GET /api/v1/admin/audit-logs.
//
// The page is the SSoT for admin + medical-staff dashboard activity logs
// (PRD 18 §1.1). FE only reads; logs are append-only per §10.3.
//
// All coded fields below carry stable English wire CODES — never display text.
// The FE maps each code to its Korean label through the backend-served label
// catalog (the i18next `labels` namespace): auditAction / auditCategory /
// auditStatus / auditSource / auditUserType / auditTarget.

// ── Enums (wire codes) ───────────────────────────────────────────────

// "출처 (Source)" discriminator. Rendered via auditSource.<code> in `labels`.
export type LogSource = 'ADMIN' | 'MEDICAL_WEB';

// "사용자 분류 (User Category)" — actorRoleKey. Role-key, NOT the dynamic Role
// name list. Rendered via auditUserType.<code> in `labels`.
export type LogUserType =
  | 'ROOT'
  | 'OPERATION'
  | 'CONTENT'
  | 'SYSTEM'
  | 'DOCTOR'
  | 'NURSE'
  // CRC is the third medical staff role, alongside DOCTOR and NURSE. It was missing here, so the
  // filter could not offer it even though real rows carry it.
  | 'CRC';

export const LOG_USER_TYPES: readonly LogUserType[] = [
  'ROOT',
  'OPERATION',
  'CONTENT',
  'SYSTEM',
  'DOCTOR',
  'NURSE',
  'CRC',
] as const;

// Three-way category. Rendered via auditCategory.<code> in `labels`.
export type LogCategory = 'ACCESS_AUTH' | 'INFO_CHANGE' | 'OPERATION_EXPORT';

export const LOG_CATEGORIES: readonly LogCategory[] = [
  'ACCESS_AUTH',
  'INFO_CHANGE',
  'OPERATION_EXPORT',
] as const;

// Status. Rendered via auditStatus.<code> in `labels`.
// Wire codes are 'SUCCESS' | 'FAIL' — the exact tokens the BE stores
// (audit_log.status CHECK IN ('SUCCESS','FAIL')) and serves labels for
// (auditStatus.SUCCESS / auditStatus.FAIL).
export type LogStatus = 'SUCCESS' | 'FAIL';

// ── Entity (AuditLogListItem) ────────────────────────────────────────

// Exact BE row shape. `actionCode` / `targetCode` / `source` / `category` /
// `status` / `actorRoleKey` are codes mapped to Korean via the label catalog.
export interface AuditLogListItem {
  id: number;
  createdAt: string;              // ISO-8601 instant. Rendered KST "YYYY.MM.DD HH:MM:SS".
  actorAdminId: number | null;
  actorName: string;
  actorEmail: string;
  actorRoleKey: string;           // LogUserType code (catalog: auditUserType).
  source: string;                 // LogSource code (catalog: auditSource).
  category: string;               // LogCategory code (catalog: auditCategory).
  actionCode: string;             // catalog: auditAction.
  targetCode: string;             // catalog: auditTarget.
  targetId: string | null;        // appended as " #${targetId}" when present.
  // SHCT-643 — the structured action detail the backend has always sent and this type used to omit.
  // `params` is the action's own context (for a PRO deployment: how many items, questions and
  // guidelines went out, whether the module configuration changed, and which disease scope was
  // published); `changes` is the before→after field diff. Both are null when the writer captured none.
  // Shapes vary by action, so they are read defensively at the point of display rather than typed per
  // action here — a wrong assumption about one action's keys would break the whole row otherwise.
  params: Record<string, unknown> | null;
  changes: Record<string, unknown>[] | null;
  ipAddress: string | null;
  status: string;                 // LogStatus code (catalog: auditStatus).
  failReasonCode: string | null;
}

// ── Sorting (SHCT-437) ───────────────────────────────────────────────

// The eight column keys the backend will sort on. They are the wire names the
// `sort` query parameter accepts — the BE holds the matching whitelist, so a
// key not in this list is ignored server-side and the default view is served.
// The IP-address column is deliberately absent: it is not sortable.
export type LogSortKey =
  | 'createdAt'
  | 'actorName'
  | 'source'
  | 'userType'
  | 'category'
  | 'actionCode'
  | 'target'
  | 'status';

export const LOG_SORT_KEYS: readonly LogSortKey[] = [
  'createdAt',
  'actorName',
  'source',
  'userType',
  'category',
  'actionCode',
  'target',
  'status',
] as const;

// Ascending or descending. A header click flips between the two (2-click cycle).
export type LogSortDir = 'asc' | 'desc';

// The default view: newest first.
export const LOG_DEFAULT_SORT: LogSortKey = 'createdAt';
export const LOG_DEFAULT_SORT_DIR: LogSortDir = 'desc';

// ── API contract ─────────────────────────────────────────────────────

// Rows per page, fixed at 20 by the customer document (PRD 18 §2.2 item 9).
export const LOG_PAGE_SIZE = 20;

// Query params for GET /admin/audit-logs. All optional. 'all'/empty filter
// values are converted to undefined by the caller (omitted = no filter).
export interface ListLogsParams {
  dateFrom?: string;              // YYYY-MM-DD (KST). Inclusive.
  dateTo?: string;                // YYYY-MM-DD (KST). Inclusive.
  source?: LogSource;
  category?: LogCategory;
  userType?: LogUserType;
  status?: LogStatus;
  search?: string;                // partial match on actorName / action / target.
  page?: number;                  // zero-based page index.
  size?: number;                  // rows per page.
  sort?: LogSortKey;              // omitted = createdAt.
  dir?: LogSortDir;               // omitted = desc.
}

// Response envelope payload. `totalElements` / `totalPages` are what let the
// screen draw numbered pages; `page` / `size` echo back which page this is.
export interface ListLogsResult {
  items: AuditLogListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// CSV export result (FEATURE_062): the server-rendered CSV bytes + the filename
// the server chose (from Content-Disposition). The FE does NOT build the CSV —
// the BE renders it (Korean labels, subject-PII masking) and streams it back.
export interface ExportLogsResult {
  blob: Blob;
  filename: string;
}

// ── Errors ───────────────────────────────────────────────────────────

export type LogInquiryApiErrorCode =
  // Validation (date range)
  | 'INVALID_DATE_RANGE'
  | 'INVALID_DATE_FORMAT'
  | 'AUDIT_DATE_RANGE_INVALID'
  // A coded filter carried a value the server does not recognise. Its Korean message is
  // user-actionable ("선택한 필터가 유효하지 않습니다."), so it is shown verbatim rather than
  // being flattened into the generic "could not load" text.
  | 'AUDIT_FILTER_VALUE_INVALID'
  // CSV export (FEATURE_062)
  | 'EXPORT_NOT_PERMITTED'
  | 'EXPORT_ROW_LIMIT_EXCEEDED'
  // No row matches the current filters, so the server produced no file at all.
  // This is information, not a failure — the screen says so and stays put.
  | 'EXPORT_NO_DATA'
  // Cross-cutting
  | 'UNAUTHORIZED'
  | 'INSUFFICIENT_PERMISSION'
  | 'INTERNAL_ERROR'
  | 'UNKNOWN';

export class LogInquiryApiError extends Error {
  readonly code: LogInquiryApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: LogInquiryApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'LogInquiryApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, LogInquiryApiError.prototype);
  }
}

// PRD §10.3 — logs are append-only. NO create / update / delete on this
// surface. `exportLogs` reads only (it produces a CSV file); the BE renders +
// audits the export (FEATURE_062).
export interface LogInquiryApi {
  listLogs(params: ListLogsParams): Promise<ListLogsResult>;
  exportLogs(params: ListLogsParams): Promise<ExportLogsResult>;
}
